import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, MagicWand, Copy, Eye, PencilSimple, Trophy, Users, CalendarBlank, X, Archive, Trash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import AIButton from "@/components/AIButton";

// Batch 39 FIX-3: badge colorati per portale + collapse responsive
const PORTAL_COLORS = {
  "LinkedIn":  "bg-[#0A66C2]/10 text-[#0A66C2] border-[#0A66C2]/30",
  "Indeed":    "bg-[#003A9B]/10 text-[#003A9B] border-[#003A9B]/30",
  "InfoJobs":  "bg-[#FF6B00]/10 text-[#FF6B00] border-[#FF6B00]/30",
  "Monster":   "bg-[#6E42C1]/10 text-[#6E42C1] border-[#6E42C1]/30",
  "Subito":    "bg-[#E20613]/10 text-[#E20613] border-[#E20613]/30",
  "Bakeca":    "bg-[#00A651]/10 text-[#00A651] border-[#00A651]/30",
  "Glassdoor": "bg-[#0CAA41]/10 text-[#0CAA41] border-[#0CAA41]/30",
  "Trovit":    "bg-[#FFB400]/10 text-[#A07000] border-[#FFB400]/40",
  "Jobrapido": "bg-[#E63946]/10 text-[#E63946] border-[#E63946]/30",
  "Talent.com":"bg-[#1B4FD8]/10 text-[#1B4FD8] border-[#1B4FD8]/30",
};
function PublishedOnBadges({ portals }) {
  if (!portals?.length) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="position-published-on">
      <span className="hidden sm:inline-flex items-center gap-1 flex-wrap">
        {portals.map((portal) => (
          <Badge key={portal}
            title={`Pubblicato su ${portal}`}
            className={`text-[10px] font-medium px-2 py-0.5 border ${PORTAL_COLORS[portal] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
            {portal}
          </Badge>
        ))}
      </span>
      <span className="sm:hidden text-[10px] font-medium px-2 py-0.5 rounded bg-[#1B4FD8]/10 text-[#1B4FD8] border border-[#1B4FD8]/30"
            title={portals.join(" · ")}>
        {portals.length} portali
      </span>
    </div>
  );
}
import PageHeader from "@/components/PageHeader";
import ResizableDialogContent from "@/components/ResizableDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = ["Vendite","Marketing","Amministrazione","Acquisti","Logistica","Magazzino","Ufficio Tecnico","Produzione","IT","HR","Legale","Finanza","Operazioni","Customer Service","Altro"];
const LEVELS = ["Addetto Junior","Addetto","Impiegato","Impiegato Senior","Quadro","Responsabile","Coordinatore","Specialista","Consulente","Dirigente"];
const CONTRACTS = ["Full-time","Part-time","Apprendistato Full-time","Apprendistato Part-time","Partita IVA"];
const WORK_MODES = ["In sede","Da remoto","Ibrido"];
const SECTORS = ["Commercio","Industria","Terziario","Artigianato","Edilizia","Trasporti","Sanita","Altro"];
const TONES = ["Molto formale","Formale","Smart","Informale","Motivazionale"];
const PORTALS = ["LinkedIn","Indeed","InfoJobs","Monster","Subito","Bakeca","Glassdoor","Trovit","Jobrapido","Talent.com"];
const FRINGE_LIST = [
  "Auto aziendale (solo uso lavorativo)","Auto aziendale (uso promiscuo)","Telefono aziendale","PC/Laptop aziendale",
  "Buoni acquisto/voucher","Alloggio aziendale","Polizza sanitaria integrativa","Buoni carburante",
  "Prestito agevolato","Rimborso utenze domestiche"
];
const BENEFIT_LIST = [
  "Ticket Restaurant/Buoni pasto","Assicurazione vita","Mensa aziendale","Formazione e aggiornamento professionale",
  "Asilo nido/Contributo asilo","Welfare aziendale","Fondo pensione integrativo","Palestra/Convenzioni sportive",
  "Rimborso spese trasporto","Flessibilita oraria"
];

const EMPTY_FORM = {
  title:"",department:"",level:"",contract_type:"",work_mode:"",location:"",contract_sector:"",months:13,
  ral_min:0,ral_max:0,show_ral:true,show_mbo:true,show_ticket:true,mbo_enabled:false,mbo_type:"fixed",mbo_amount:"",mbo_period:"Annuale",
  fringe_benefits:[],benefits:[],fringe_other:"",benefit_other:"",ticket_amount:"",
  is_public:true,tone:"Formale",internal_notes:"",status:"Bozza",job_description:"",
  piva_fixed:false,piva_fixed_amount:"",piva_expense:false,piva_expense_type:"Forfettario",piva_expense_amount:"",
  deadline:"",published_on:[],jd_snapshot:"",
  linked_job_description_id:"",jd_link_live:false
};

// Build a readable job_description markdown from a mansionario library entry
function jdFromMansionario(m) {
  const sec = (title, arr) => {
    if (!arr || arr.length === 0) return "";
    return `\n## ${title}\n${arr.map(x => `- ${x}`).join("\n")}\n`;
  };
  let md = `# ${m.title}\n`;
  if (m.macro_area) md += `\n*Area:* ${m.macro_area}\n`;
  md += sec("Responsabilità", m.responsibilities);
  md += sec("Competenze tecniche", m.technical_skills);
  md += sec("Competenze soft", m.soft_skills);
  md += sec("KPI", m.kpi);
  if (m.notes) md += `\n## Note\n${m.notes}\n`;
  return md.trim();
}

function CheckboxGroup({ items, selected, onChange, otherValue, onOtherChange, otherLabel = "Altro" }) {
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {items.map(item => (
        <label key={item} className="flex items-center gap-2 text-xs text-[#0D1F3C]/70 cursor-pointer hover:text-[#0D1F3C] transition-colors">
          <Checkbox checked={selected.includes(item)} onCheckedChange={(checked) => {
            onChange(checked ? [...selected, item] : selected.filter(i => i !== item));
          }} />
          {item}
        </label>
      ))}
      <label className="flex items-center gap-2 text-xs text-[#0D1F3C]/70 cursor-pointer col-span-2">
        <Checkbox checked={selected.includes("Altro")} onCheckedChange={(checked) => {
          onChange(checked ? [...selected, "Altro"] : selected.filter(i => i !== "Altro"));
        }} />
        {otherLabel}
      </label>
      {selected.includes("Altro") && (
        <Input className="col-span-2 h-8 text-xs" placeholder="Specifica..." value={otherValue} onChange={e => onOtherChange(e.target.value)} />
      )}
    </div>
  );
}

function PortalMultiSelect({ selected, onChange }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {PORTALS.map(p => (
          <Badge key={p} variant={selected.includes(p) ? "default" : "outline"}
            className={`text-[10px] cursor-pointer transition-all ${selected.includes(p) ? 'bg-[#1B4FD8] text-white' : 'hover:border-[#1B4FD8]/30'}`}
            onClick={() => onChange(selected.includes(p) ? selected.filter(x => x !== p) : [...selected, p])}>
            {p}
          </Badge>
        ))}
        <Badge variant={selected.some(s => !PORTALS.includes(s)) ? "default" : "outline"}
          className={`text-[10px] cursor-pointer ${selected.some(s => !PORTALS.includes(s)) ? 'bg-[#1B4FD8] text-white' : ''}`}
          onClick={() => {
            if (!selected.some(s => !PORTALS.includes(s))) onChange([...selected, ""]);
          }}>
          + Altro
        </Badge>
      </div>
      {selected.some(s => !PORTALS.includes(s)) && (
        <Input className="h-8 text-xs" placeholder="Nome portale personalizzato..." value={selected.find(s => !PORTALS.includes(s)) || ""}
          onChange={e => {
            const custom = e.target.value;
            const filtered = selected.filter(s => PORTALS.includes(s));
            if (custom) filtered.push(custom);
            onChange(filtered);
          }} />
      )}
    </div>
  );
}

export default function PositionsPage() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("aperte"); // aperte | archiviate | cestino
  const [confirmAction, setConfirmAction] = useState(null); // {type: "delete"|"archive", pos}
  const [deleteCheck, setDeleteCheck] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewJD, setViewJD] = useState(null);
  const [editJD, setEditJD] = useState("");
  const [editJDId, setEditJDId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [form, setForm] = useState({...EMPTY_FORM});
  const [originalForm, setOriginalForm] = useState(null); // BUG-19: snapshot per warning campi critici
  const [regenerateOnSave, setRegenerateOnSave] = useState(false); // BUG-19: default OFF
  const [previewJDText, setPreviewJDText] = useState(null); // null | string
  const [previewBusy, setPreviewBusy] = useState(false);
  const [deptFilterPos, setDeptFilterPos] = useState("");
  const [locFilter, setLocFilter] = useState("");

  // Mansionari library autocomplete (Task 3)
  const { user } = useAuth();
  const [jdLibrary, setJdLibrary] = useState([]);
  const [jdSearch, setJdSearch] = useState("");
  const [jdPickerOpen, setJdPickerOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const wsId = user?.active_workspace_id;
    if (!wsId) return;
    axios.get(`${API}/library/job-descriptions`, { params: { workspace_id: wsId }, withCredentials: true })
      .then(r => setJdLibrary(r.data || []))
      .catch(() => setJdLibrary([]));
  }, [open, user?.active_workspace_id]);
  const filteredJd = (jdSearch.trim() === "")
    ? jdLibrary.slice(0, 30)
    : jdLibrary.filter(m => (m.title || "").toLowerCase().includes(jdSearch.toLowerCase())).slice(0, 30);
  const pickMansionario = (m) => {
    setForm(p => ({
      ...p,
      title: p.title || m.title || "",
      job_description: jdFromMansionario(m),
      linked_job_description_id: m.id,
    }));
    setJdPickerOpen(false);
    setJdSearch("");
    toast.success(`Mansionario "${m.title}" applicato`);
  };
  const unlinkMansionario = () => setForm(p => ({ ...p, linked_job_description_id: "", jd_link_live: false }));

  const load = useCallback(() => {
    axios.get(`${API}/positions?status=${statusFilter}`, { withCredentials: true }).then(r => setPositions(r.data)).catch(() => {});
    axios.get(`${API}/candidates`, { withCredentials: true }).then(r => setCandidates(r.data)).catch(() => {});
  }, [statusFilter]);
  // Batch 62.A: re-load on workspace switch for agency accounts
  useEffect(() => { load(); }, [load, user?.active_workspace_id]); // eslint-disable-line react-hooks/exhaustive-deps
  // B71 FIX-3: refetch on focus
  useRefetchOnFocus(load);

  // Action handlers (Batch 40)
  const doArchive = async () => {
    if (!confirmAction || busyAction) return;
    setBusyAction(true);
    try {
      await axios.post(`${API}/positions/${confirmAction.pos.id}/archive`, {}, { withCredentials: true });
      toast.success("Posizione archiviata");
      setConfirmAction(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusyAction(false); }
  };
  const doDelete = async () => {
    if (!confirmAction || busyAction || !deleteCheck) return;
    setBusyAction(true);
    try {
      await axios.delete(`${API}/positions/${confirmAction.pos.id}`, { withCredentials: true });
      toast.success("Posizione spostata nel cestino (30 giorni residui)");
      setConfirmAction(null); setDeleteCheck(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusyAction(false); }
  };
  const doRestore = async (pid) => {
    try {
      await axios.post(`${API}/positions/${pid}/restore`, {}, { withCredentials: true });
      toast.success("Posizione ripristinata"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doUnarchive = async (pid) => {
    try {
      await axios.post(`${API}/positions/${pid}/unarchive`, {}, { withCredentials: true });
      toast.success("Posizione riaperta"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const daysLeft = (deletedAt) => {
    if (!deletedAt) return null;
    const dt = new Date(deletedAt);
    const expires = new Date(dt.getTime() + 30 * 24 * 60 * 60 * 1000);
    return Math.max(0, Math.ceil((expires - new Date()) / (1000 * 60 * 60 * 24)));
  };

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const resetForm = () => { setForm({...EMPTY_FORM}); setEditId(null); setOriginalForm(null); setRegenerateOnSave(false); };

  const save = async () => {
    try {
      let serverJd = null;
      if (editId) {
        const { data } = await axios.put(`${API}/positions/${editId}`,
          { ...form, regenerate_jd: regenerateOnSave }, { withCredentials: true });
        serverJd = data?.job_description || null;
      } else {
        const { data } = await axios.post(`${API}/positions`, form, { withCredentials: true });
        serverJd = data?.job_description || null;
      }
      toast.success(editId
        ? (regenerateOnSave ? "Posizione aggiornata · JD rigenerata" : "Posizione aggiornata")
        : "Posizione creata · JD generata automaticamente");
      setOpen(false); resetForm(); load();
      if (!editId && serverJd) {
        // Mostra subito la JD generata per la nuova posizione
        setViewJD(serverJd); setEditJD(serverJd);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const previewJD = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await axios.post(`${API}/positions/preview-jd`, form, { withCredentials: true });
      setPreviewJDText(data.job_description || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore anteprima JD");
    } finally { setPreviewBusy(false); }
  };

  const generateJD = async (pid) => {
    setGeneratingId(pid);
    try {
      const { data } = await axios.post(`${API}/positions/${pid}/generate-jd`, {}, { withCredentials: true });
      toast.success("Job Description generata");
      load();
      setViewJD(data.job_description);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella generazione");
    } finally { setGeneratingId(null); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(`${window.location.origin}/apply/${link}`);
    toast.success("Link copiato");
  };

  const statusColor = { Bozza: "bg-gray-100 text-gray-600", Aperta: "bg-green-50 text-green-700", Chiusa: "bg-red-50 text-red-600" };

  const filteredPositions = positions.filter(p => {
    if (deptFilterPos && deptFilterPos !== "all" && p.department !== deptFilterPos) return false;
    if (locFilter && locFilter !== "all" && p.location !== locFilter) return false;
    return true;
  });

  const formatDateIT = (iso) => {
    if (!iso) return "";
    const p = iso.split("T")[0].split("-");
    if (p.length === 3 && p[0].length === 4) return `${p[2]}/${p[1]}/${p[0]}`;
    return iso;
  };

  return (
    <div className="animate-fade-in" data-testid="positions-page">
      <PageHeader title="Posizioni Aperte" subtitle={`${positions.length} ${statusFilter === "cestino" ? "nel cestino" : statusFilter === "archiviate" ? "archiviate" : "posizioni totali"}`} />
      {/* Tabs filtro stato (Batch 40) */}
      <div className="flex items-center gap-1 mb-4 border-b border-[#0D1F3C]/8" data-testid="positions-status-tabs">
        {[
          { v: "aperte", label: "Aperte" },
          { v: "archiviate", label: "Archiviate" },
          { v: "cestino", label: "Cestino" },
        ].map((t) => (
          <button key={t.v} onClick={() => setStatusFilter(t.v)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              statusFilter === t.v
                ? "border-[#1B4FD8] text-[#1B4FD8]"
                : "border-transparent text-[#0D1F3C]/50 hover:text-[#0D1F3C]"
            }`}
            data-testid={`status-tab-${t.v}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {/* Filters */}
          <Select value={deptFilterPos} onValueChange={setDeptFilterPos}>
            <SelectTrigger className="w-[180px] h-10"><SelectValue placeholder="Tutti i reparti" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i reparti</SelectItem>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={locFilter} onValueChange={setLocFilter}>
            <SelectTrigger className="w-[180px] h-10"><SelectValue placeholder="Tutte le sedi" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le sedi</SelectItem>{[...new Set(positions.map(p=>p.location).filter(Boolean))].map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={o => { setOpen(o); if(!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-position-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]"><Plus size={18} className="mr-2" />Nuova Posizione</Button>
          </DialogTrigger>
          <ResizableDialogContent defaultW="70vw" defaultH="85vh">
            <DialogHeader><DialogTitle>{editId ? "Modifica Posizione" : "Nuova Posizione"}</DialogTitle></DialogHeader>
            <div className="space-y-5 mt-4">

              {/* PARTI DA UN MANSIONARIO (optional) */}
              <div className="rounded-lg border border-[#1B4FD8]/20 bg-[#1B4FD8]/[0.03] p-4" data-testid="mansionario-picker">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-xs font-semibold uppercase text-[#1B4FD8] tracking-wider">Parti da un mansionario <span className="text-[#0D1F3C]/40 normal-case font-normal">(opzionale)</span></h3>
                    <p className="text-[11px] text-[#0D1F3C]/60 mt-0.5">Seleziona un mansionario dalla library per pre-popolare titolo e descrizione.</p>
                  </div>
                </div>

                {/* Linked mansionario card — shown when a mansionario is selected */}
                {(() => {
                  if (!form.linked_job_description_id) return null;
                  const linkedM = jdLibrary.find(x => x.id === form.linked_job_description_id);
                  if (!linkedM) return null;
                  const badgeText = linkedM.is_standard ? "Standard" : (linkedM.is_ai_generated ? "Auto" : "Custom");
                  const badgeColor = linkedM.is_standard ? "bg-blue-100 text-blue-700"
                                     : linkedM.is_ai_generated ? "bg-violet-100 text-violet-700"
                                     : "bg-slate-100 text-slate-700";
                  const createdDate = linkedM.created_at
                    ? new Date(linkedM.created_at).toLocaleDateString("it-IT")
                    : null;
                  const currentJd = jdFromMansionario(linkedM);
                  const drift = (form.job_description || "").trim() !== currentJd.trim();
                  return (
                    <div
                      className="rounded-lg border border-[#1B4FD8]/30 bg-gradient-to-br from-[#1B4FD8]/5 to-[#56A3F5]/5 p-4 mb-3"
                      data-testid="linked-mansionario-card"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#1B4FD8]">
                            <span className="inline-block w-4 h-4 rounded bg-[#1B4FD8]/15 flex items-center justify-center">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                            </span>
                            Mansionario collegato
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badgeColor}`}>{badgeText}</span>
                            <span className="text-[15px] font-semibold text-[#0D1F3C] truncate" data-testid="linked-mansionario-title">{linkedM.title}</span>
                          </div>
                          {(createdDate || linkedM.macro_area) && (
                            <div className="text-[11px] text-[#0D1F3C]/50 mt-1">
                              {linkedM.macro_area && <span>{linkedM.macro_area}</span>}
                              {createdDate && linkedM.macro_area && <span className="mx-1.5">·</span>}
                              {createdDate && <span>Creato il {createdDate}</span>}
                            </div>
                          )}
                          <label className="flex items-center gap-2 mt-3 text-xs text-[#0D1F3C]/70 cursor-pointer">
                            <Checkbox
                              checked={!!form.jd_link_live}
                              onCheckedChange={v => up("jd_link_live", !!v)}
                              data-testid="jd-link-live-checkbox"
                            />
                            Mantieni link live con mansionario
                            <span className="text-[#0D1F3C]/40">(modifiche al mansionario aggiornano la posizione)</span>
                          </label>
                          {drift && (
                            <p className="mt-2 text-[11px] text-[#F59E0B] italic" data-testid="drift-warning">
                              ⚠ I campi sono stati modificati manualmente: il link resta attivo ma i dati differiscono dal mansionario originale.
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={unlinkMansionario}
                            className="h-7 px-2.5 text-[11px] border-red-200 text-red-600 hover:bg-red-50"
                            data-testid="unlink-mansionario-btn"
                          >
                            <X size={12} className="mr-1" />Scollega
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => { setOpen(false); navigate(`/settings?tab=mansionari&id=${linkedM.id}`); }}
                            className="h-7 px-2.5 text-[11px] border-[#1B4FD8]/30 text-[#1B4FD8] hover:bg-[#1B4FD8]/5"
                            data-testid="open-mansionario-btn"
                          >
                            Apri ↗
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Search autocomplete input */}
                <div className="relative">
                  <Input
                    data-testid="mansionario-search-input"
                    placeholder={form.linked_job_description_id ? "Cambia mansionario…" : "Cerca mansionario per titolo..."}
                    value={jdSearch}
                    onFocus={() => setJdPickerOpen(true)}
                    onBlur={() => setTimeout(() => setJdPickerOpen(false), 200)}
                    onChange={e => { setJdSearch(e.target.value); setJdPickerOpen(true); }}
                    className="h-9 text-sm"
                  />
                  {jdPickerOpen && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-[#0D1F3C]/10 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="mansionario-dropdown">
                      {filteredJd.length === 0 && <div className="p-3 text-xs text-[#0D1F3C]/50">Nessun mansionario trovato</div>}
                      {filteredJd.map(m => {
                        const badgeText = m.is_standard ? "Standard" : (m.is_ai_generated ? "Auto" : "Custom");
                        const badgeColor = m.is_standard ? "bg-slate-100 text-slate-700"
                                           : m.is_ai_generated ? "bg-violet-100 text-violet-700"
                                           : "bg-emerald-100 text-emerald-700";
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); pickMansionario(m); }}
                            className="w-full text-left px-3 py-2 hover:bg-[#1B4FD8]/5 border-b border-[#0D1F3C]/5 last:border-0 flex items-center justify-between gap-2"
                            data-testid={`mansionario-option-${m.id}`}
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-[#0D1F3C] truncate">{m.title}</div>
                              {m.macro_area && <div className="text-[11px] text-[#0D1F3C]/50">{m.macro_area}</div>}
                            </div>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded ${badgeColor} flex-shrink-0`}>{badgeText}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* DATI PRINCIPALI */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-[#0D1F3C]/40 tracking-wider mb-3">Dati Principali</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Titolo Ruolo *</Label><Input data-testid="pos-title" value={form.title} onChange={e=>up("title",e.target.value)} placeholder="es. Responsabile Commerciale" /></div>
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Reparto *</Label>
                    <Select value={form.department} onValueChange={v=>up("department",v)}><SelectTrigger data-testid="pos-department"><SelectValue placeholder="Seleziona reparto" /></SelectTrigger><SelectContent>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Livello *</Label>
                    <Select value={form.level} onValueChange={v=>up("level",v)}><SelectTrigger data-testid="pos-level"><SelectValue placeholder="Seleziona livello" /></SelectTrigger><SelectContent>{LEVELS.map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Contratto</Label>
                    <Select value={form.contract_type} onValueChange={v=>up("contract_type",v)}><SelectTrigger data-testid="pos-contract"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{CONTRACTS.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Modalita Lavoro</Label>
                    <Select value={form.work_mode} onValueChange={v=>up("work_mode",v)}><SelectTrigger data-testid="pos-workmode"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{WORK_MODES.map(w=><SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent></Select></div>
                </div>
              </div>

              {/* SEDE */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-[#0D1F3C]/40 tracking-wider mb-3">Sede e Modalita</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Sede di Lavoro</Label><Input value={form.location} onChange={e=>up("location",e.target.value)} placeholder="es. Milano, Roma" /></div>
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Deadline Inserimento</Label><Input type="date" value={form.deadline} onChange={e=>up("deadline",e.target.value)} data-testid="pos-deadline" /></div>
                </div>
              </div>

              {/* P.IVA SECTION (conditional) */}
              {form.contract_type === "Partita IVA" && (
                <div className="border border-[#F59E0B]/20 rounded-lg p-4 bg-[#F59E0B]/[0.02]">
                  <h3 className="text-xs font-semibold uppercase text-[#F59E0B] tracking-wider mb-3">Dettagli Partita IVA</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2"><Switch checked={form.piva_fixed} onCheckedChange={v=>up("piva_fixed",v)} /><Label className="text-xs text-[#0D1F3C]/60">Compenso fisso mensile</Label></div>
                      {form.piva_fixed && <Input type="number" placeholder="Importo fisso (€)" value={form.piva_fixed_amount} onChange={e=>up("piva_fixed_amount",e.target.value)} className="h-8 text-xs" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2"><Switch checked={form.piva_expense} onCheckedChange={v=>up("piva_expense",v)} /><Label className="text-xs text-[#0D1F3C]/60">Rimborso spese</Label></div>
                      {form.piva_expense && (
                        <div className="space-y-2">
                          <Select value={form.piva_expense_type} onValueChange={v=>up("piva_expense_type",v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="Forfettario">Forfettario</SelectItem><SelectItem value="A pie di lista">A pie di lista</SelectItem></SelectContent>
                          </Select>
                          {form.piva_expense_type === "Forfettario" && <Input type="number" placeholder="Importo mensile (€)" value={form.piva_expense_amount} onChange={e=>up("piva_expense_amount",e.target.value)} className="h-8 text-xs" />}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* RETRIBUZIONE */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-[#0D1F3C]/40 tracking-wider mb-3">Retribuzione</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">RAL Min (€)</Label><Input type="number" value={form.ral_min || ""} onChange={e=>up("ral_min",parseFloat(e.target.value)||0)} placeholder="es. 25000" /></div>
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">RAL Max (€)</Label><Input type="number" value={form.ral_max || ""} onChange={e=>up("ral_max",parseFloat(e.target.value)||0)} placeholder="es. 35000" /></div>
                  <div className="flex items-end gap-4 pb-1">
                    <div className="flex items-center gap-2"><Switch checked={form.show_ral} onCheckedChange={v=>up("show_ral",v)} /><Label className="text-xs text-[#0D1F3C]/60">RAL nella JD</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={form.show_mbo} onCheckedChange={v=>up("show_mbo",v)} /><Label className="text-xs text-[#0D1F3C]/60">MBO nella JD</Label></div>
                    <div className="flex items-center gap-2"><Switch checked={form.show_ticket} onCheckedChange={v=>up("show_ticket",v)} /><Label className="text-xs text-[#0D1F3C]/60">Ticket nella JD</Label></div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Mensilita</Label>
                    <Select value={String(form.months)} onValueChange={v=>up("months",parseInt(v))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="13">13 mensilita</SelectItem><SelectItem value="14">14 mensilita</SelectItem></SelectContent></Select></div>
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Settore Contrattuale</Label>
                    <Select value={form.contract_sector} onValueChange={v=>up("contract_sector",v)}><SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{SECTORS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                  <div />
                </div>

                {/* MBO */}
                <div className="mt-4 border border-[#0D1F3C]/8 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3"><Switch checked={form.mbo_enabled} onCheckedChange={v=>up("mbo_enabled",v)} /><Label className="text-xs font-medium text-[#0D1F3C]">MBO / Premio variabile</Label></div>
                  {form.mbo_enabled && (
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Tipo</Label>
                        <Select value={form.mbo_type || "fixed"} onValueChange={v=>up("mbo_type",v)}>
                          <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="fixed">Importo fisso (€)</SelectItem><SelectItem value="percentage">% sulla RAL</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">{form.mbo_type === "percentage" ? "Percentuale (%)" : "Importo (€)"}</Label>
                        <Input type="number" value={form.mbo_amount} onChange={e=>up("mbo_amount",e.target.value)} placeholder={form.mbo_type === "percentage" ? "es. 5" : "es. 10000"} /></div>
                      <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Periodo</Label>
                        <Select value={form.mbo_period || "Annuale"} onValueChange={v=>up("mbo_period",v)}>
                          <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="Annuale">Annuale</SelectItem><SelectItem value="Semestrale">Semestrale</SelectItem><SelectItem value="Trimestrale">Trimestrale</SelectItem><SelectItem value="Mensile">Mensile</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* FRINGE BENEFIT */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-[#0D1F3C]/40 tracking-wider mb-3">Fringe Benefit</h3>
                <CheckboxGroup items={FRINGE_LIST} selected={form.fringe_benefits} onChange={v => {
                  const autoLav = "Auto aziendale (solo uso lavorativo)";
                  const autoPro = "Auto aziendale (uso promiscuo)";
                  if (v.includes(autoLav) && v.includes(autoPro)) {
                    const lastAdded = v[v.length - 1];
                    toast.error("Puoi selezionare solo un tipo di auto aziendale");
                    up("fringe_benefits", v.filter(x => x !== (lastAdded === autoLav ? autoPro : autoLav)));
                  } else { up("fringe_benefits", v); }
                }}
                  otherValue={form.fringe_other} onOtherChange={v=>up("fringe_other",v)} otherLabel="Altro fringe benefit" />
              </div>

              {/* BENEFIT */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-[#0D1F3C]/40 tracking-wider mb-3">Benefit</h3>
                <CheckboxGroup items={BENEFIT_LIST} selected={form.benefits} onChange={v=>up("benefits",v)}
                  otherValue={form.benefit_other} onOtherChange={v=>up("benefit_other",v)} otherLabel="Altro benefit" />
                {form.benefits.includes("Ticket Restaurant/Buoni pasto") && (
                  <div className="mt-2"><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Importo Ticket (€)</Label>
                    <Input type="number" className="w-40 h-8 text-xs" value={form.ticket_amount} onChange={e=>up("ticket_amount",e.target.value)} placeholder="es. 7" /></div>
                )}
              </div>

              {/* PUBBLICAZIONE + TONO + STATO */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-[#0D1F3C]/40 tracking-wider mb-3">Pubblicazione</h3>
                <div className="mb-3"><Label className="text-xs text-[#0D1F3C]/60 mb-1.5 block">Pubblicato su</Label>
                  <PortalMultiSelect selected={form.published_on || []} onChange={v=>up("published_on",v)} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Tono JD</Label>
                    <Select value={form.tone} onValueChange={v=>up("tone",v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TONES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Stato</Label>
                    <Select value={form.status} onValueChange={v=>up("status",v)}><SelectTrigger data-testid="pos-status"><SelectValue /></SelectTrigger><SelectContent>{["Bozza","Aperta","Chiusa"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="flex items-center gap-2 mt-3"><Switch checked={form.is_public} onCheckedChange={v=>up("is_public",v)} /><Label className="text-xs text-[#0D1F3C]/60">Ricerca pubblica (mostra nome azienda)</Label></div>
              </div>

              <div><Label className="text-xs text-[#0D1F3C]/60 mb-1 block">Note Interne</Label><Textarea value={form.internal_notes} onChange={e=>up("internal_notes",e.target.value)} rows={2} /></div>

              {/* Batch 41-bugfix: Anteprima JD + Rigenera al salvataggio (BUG-18: AIButton riusabile · BUG-19: checkbox dinamica) */}
              <div className="flex flex-col gap-3 pt-2 border-t border-[#0D1F3C]/8">
                {editId && (() => {
                  const JD_CRITICAL_FIELDS = ["title","department","level","contract_type","work_mode","location","contract_sector","months","ral_min","ral_max","show_ral","show_mbo","show_ticket","mbo_enabled","mbo_type","mbo_amount","mbo_period","ticket_amount","fringe_benefits","benefits","fringe_other","benefit_other","published_on","tone","linked_job_description_id"];
                  const arrEq = (a, b) => JSON.stringify((a||[]).slice().sort()) === JSON.stringify((b||[]).slice().sort());
                  const isModified = (k) => {
                    if (!originalForm) return false;
                    const a = form[k], b = originalForm[k];
                    if (Array.isArray(a) || Array.isArray(b)) return !arrEq(a, b);
                    return (a ?? "") !== (b ?? "");
                  };
                  const modifiedFields = JD_CRITICAL_FIELDS.filter(isModified);
                  const showWarning = modifiedFields.length > 0;
                  // Auto-suggest checkbox ON quando user modifica field critici (NON forza)
                  if (showWarning && !regenerateOnSave && !window.__sellyAutoSuggested) {
                    window.__sellyAutoSuggested = true;
                    setTimeout(() => setRegenerateOnSave(true), 0);
                  }
                  if (!showWarning && window.__sellyAutoSuggested) {
                    delete window.__sellyAutoSuggested;
                  }
                  return (
                    <>
                      {showWarning && (
                        <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="jd-fields-warning">
                          <span>⚠ Hai modificato dati che impattano la JD ({modifiedFields.slice(0,4).join(", ")}{modifiedFields.length > 4 ? "..." : ""}). Rigenerare la JD?</span>
                        </div>
                      )}
                      <label className="flex items-center gap-2 text-xs text-[#0D1F3C]/70 cursor-pointer" data-testid="regen-jd-toggle">
                        <Checkbox checked={regenerateOnSave} onCheckedChange={(v)=>setRegenerateOnSave(!!v)} />
                        <span>Rigenera la Job Description al salvataggio (consuma 1 elaborazione).</span>
                      </label>
                    </>
                  );
                })()}
                <div className="grid grid-cols-2 gap-2">
                  <AIButton
                    onClick={previewJD}
                    label="Anteprima JD"
                    loadingLabel="Generazione anteprima..."
                    successLabel="Anteprima generata"
                    errorPrefix="Errore anteprima"
                    variant="outline-primary"
                    disabled={!form.title}
                    dataTestid="preview-jd-btn"
                  />
                  <AIButton
                    onClick={save}
                    label={editId ? (regenerateOnSave ? "Aggiorna e Rigenera JD" : "Aggiorna") : "Crea Posizione"}
                    loadingLabel={editId
                      ? (regenerateOnSave ? "Rigenerazione JD..." : "Salvataggio...")
                      : "Creazione + JD..."}
                    successLabel={null}
                    errorPrefix="Errore salvataggio"
                    variant="primary"
                    disabled={!form.title}
                    dataTestid="pos-save-btn"
                  />
                </div>
              </div>
            </div>
          </ResizableDialogContent>
        </Dialog>
      </div>

      {/* JD Viewer/Editor */}
      <Dialog open={!!viewJD} onOpenChange={() => setViewJD(null)}>
        <ResizableDialogContent defaultW="70vw" defaultH="80vh">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Job Description</DialogTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="text-xs" title="Copia testo"
                  onClick={() => { navigator.clipboard.writeText(editJD); toast.success("Testo JD copiato"); }}>
                  <Copy size={14} className="mr-1" />Copia
                </Button>
                {editJDId && (
                  <Button size="sm" className="text-xs bg-[#1B4FD8] hover:bg-[#0D1F3C]"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/positions/${editJDId}`, { job_description: editJD }, { withCredentials: true });
                        toast.success("JD salvata"); load();
                      } catch { toast.error("Errore salvataggio"); }
                    }}>Salva modifiche</Button>
                )}
              </div>
            </div>
          </DialogHeader>
          <Textarea className="mt-4 text-sm text-[#0D1F3C]/80 leading-relaxed min-h-[400px] font-mono" value={editJD} onChange={e => setEditJD(e.target.value)} />
        </ResizableDialogContent>
      </Dialog>

      {/* JD Preview Dialog (Batch 41-bugfix) */}
      <Dialog open={previewJDText !== null} onOpenChange={(o)=>{ if (!o) setPreviewJDText(null); }}>
        <ResizableDialogContent defaultW="70vw" defaultH="80vh">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Anteprima Job Description</DialogTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="text-xs"
                  onClick={() => { navigator.clipboard.writeText(previewJDText || ""); toast.success("Anteprima copiata"); }}>
                  <Copy size={14} className="mr-1" />Copia
                </Button>
                <Button size="sm" className="text-xs bg-[#1B4FD8] hover:bg-[#0D1F3C]"
                  onClick={() => { up("job_description", previewJDText || ""); setPreviewJDText(null); toast.success("Anteprima applicata al form"); }}
                  data-testid="apply-preview-jd">
                  Usa questa JD
                </Button>
              </div>
            </div>
          </DialogHeader>
          <Textarea readOnly className="mt-4 text-sm text-[#0D1F3C]/80 leading-relaxed min-h-[400px] font-mono"
            value={previewJDText || ""} data-testid="preview-jd-textarea" />
        </ResizableDialogContent>
      </Dialog>

      {/* Positions List */}
      <div className="space-y-3">
        {filteredPositions.length === 0 ? <Card className="border-[#0D1F3C]/8"><CardContent className="py-12 text-center"><p className="text-[#0D1F3C]/40 text-sm">Nessuna posizione trovata.</p></CardContent></Card> :
          filteredPositions.map(p => {
            const posCands = candidates.filter(c => c.position_id === p.id);
            const withTest = posCands.filter(c => c.scores);
            const hasJD = !!p.job_description;
            const hasChanges = hasJD && p.jd_snapshot && (() => {
              const keys = ["title","department","level","contract_type","work_mode","location","contract_sector","months","ral_min","ral_max","mbo_enabled","mbo_type","mbo_amount","mbo_period","fringe_benefits","benefits","piva_fixed","piva_fixed_amount","piva_expense","piva_expense_type","piva_expense_amount"];
              const current = JSON.stringify(Object.fromEntries(keys.map(k => [k, p[k]])), Object.keys(Object.fromEntries(keys.map(k => [k, p[k]]))).sort());
              return current !== p.jd_snapshot;
            })();
            return (
            <Card key={p.id} className="border-[#0D1F3C]/8 hover:border-[#1B4FD8]/30 hover:shadow-md transition-all duration-200" data-testid={`position-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-[#0D1F3C]">{p.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[p.status] || ''}`}>{p.status}</span>
                      {p.deadline && <span className="text-[10px] text-[#0D1F3C]/40 flex items-center gap-1"><CalendarBlank size={12} />Entro {formatDateIT(p.deadline)}</span>}
                    </div>
                    <p className="text-sm text-[#0D1F3C]/50">{[p.department, p.level, p.location, p.contract_type].filter(Boolean).join(" · ")}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {posCands.length > 0 && <span className="text-xs text-[#F59E0B] font-semibold flex items-center gap-1"><Users size={14} />{posCands.length} candidati</span>}
                      {withTest.length > 0 && <span className="text-xs text-[#10B981] font-medium">{withTest.length} con analisi</span>}
                      {(p.published_on || []).length > 0 && (
                        <PublishedOnBadges portals={p.published_on} />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {posCands.length > 0 && (
                      <Button variant="outline" size="sm" onClick={() => navigate(`/candidates?position=${p.id}`)} data-testid={`cands-pos-${p.id}`}
                        className="text-xs border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#F59E0B]/5" title="Vedi candidati per questa posizione">
                        <Users size={14} className="mr-1" />{posCands.length}
                      </Button>
                    )}
                    {p.status === "Aperta" && <Button variant="outline" size="sm" onClick={()=>copyLink(p.application_link)} data-testid={`copy-link-${p.id}`} className="text-xs" title="Copia link candidatura"><Copy size={14} className="mr-1" />Link candidatura</Button>}
                    <Button variant="outline" size="sm" onClick={()=>{
                      const next = {...EMPTY_FORM, ...p, published_on: p.published_on || [], fringe_benefits: p.fringe_benefits || [], benefits: p.benefits || []};
                      setForm(next);
                      setOriginalForm(next); // BUG-19: snapshot per detect campi critici modificati
                      setRegenerateOnSave(false); // default OFF in edit
                      setEditId(p.id); setOpen(true);
                    }} className="text-xs" title="Modifica posizione"><PencilSimple size={14} className="mr-1" />Modifica</Button>
                    {hasJD && <Button variant="outline" size="sm" onClick={()=>{setViewJD(p.job_description);setEditJD(p.job_description);setEditJDId(p.id);}} className="text-xs" title="Visualizza Job Description"><Eye size={14} className="mr-1" />JD</Button>}
                    {(!hasJD || hasChanges) && (
                      <AIButton
                        onClick={async () => { await generateJD(p.id); }}
                        label={hasJD ? "Rigenera JD" : "Genera JD"}
                        loadingLabel="Generazione..."
                        successLabel={null}
                        errorPrefix="Errore generazione"
                        icon={<MagicWand size={14} />}
                        size="sm"
                        className="text-xs bg-[#56A3F5]/10 text-[#1B4FD8] hover:bg-[#56A3F5]/20 border border-[#56A3F5]/30"
                        dataTestid={`gen-jd-${p.id}`}
                      />
                    )}
                    {/* Batch 40: archive/delete/restore actions */}
                    {statusFilter === "aperte" && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setConfirmAction({ type: "archive", pos: p })} className="text-xs" title="Archivia posizione" data-testid={`archive-${p.id}`}>
                          <Archive size={14} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setConfirmAction({ type: "delete", pos: p }); setDeleteCheck(false); }}
                          className="text-xs border-red-300 text-red-700 hover:bg-red-50" title="Elimina posizione" data-testid={`delete-${p.id}`}>
                          <Trash size={14} />
                        </Button>
                      </>
                    )}
                    {statusFilter === "archiviate" && (
                      <Button variant="outline" size="sm" onClick={() => doUnarchive(p.id)}
                        className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" title="Riapri" data-testid={`unarchive-${p.id}`}>
                        <ArrowCounterClockwise size={14} className="mr-1" />Ripristina
                      </Button>
                    )}
                    {statusFilter === "cestino" && (
                      <>
                        <span className="text-[11px] text-amber-700 font-medium px-2" data-testid={`days-left-${p.id}`}>
                          {daysLeft(p.deleted_at)} giorni residui
                        </span>
                        <Button variant="outline" size="sm" onClick={() => doRestore(p.id)}
                          className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`restore-${p.id}`}>
                          <ArrowCounterClockwise size={14} className="mr-1" />Ripristina
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );})}
        {positions.length === 0 && (
          <p className="text-center py-12 text-sm text-[#0D1F3C]/40" data-testid="positions-empty-state">
            {statusFilter === "archiviate" ? "Nessuna posizione archiviata"
              : statusFilter === "cestino" ? "Cestino vuoto"
              : "Nessuna posizione aperta. Crea la prima!"}
          </p>
        )}
      </div>

      {/* Dialog conferma archive/delete (Batch 40) */}
      <Dialog open={!!confirmAction} onOpenChange={(o) => !o && !busyAction && (setConfirmAction(null), setDeleteCheck(false))}>
        <DialogContent data-testid="position-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === "archive" ? "Archivia posizione" : "Elimina posizione"}
            </DialogTitle>
          </DialogHeader>
          {confirmAction?.type === "archive" ? (
            <div className="space-y-3 text-sm">
              <p className="text-[#0D1F3C]/80">
                Archiviare la posizione <strong>{confirmAction?.pos?.title}</strong>? Sara spostata in Archiviate ma restera accessibile.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={busyAction}>Annulla</Button>
                <Button onClick={doArchive} disabled={busyAction} className="bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="confirm-archive-btn">
                  {busyAction ? "Archiviazione..." : "Conferma archivio"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <p className="text-[#0D1F3C]/80">
                Eliminare la posizione <strong>{confirmAction?.pos?.title}</strong>? Sara spostata nel cestino per 30 giorni, poi eliminata definitivamente.
                I candidati associati resteranno disponibili nello storico.
              </p>
              <div className="flex items-start gap-2">
                <Checkbox id="del-pos-check" checked={deleteCheck} onCheckedChange={setDeleteCheck} data-testid="confirm-delete-check" />
                <label htmlFor="del-pos-check" className="text-xs text-[#0D1F3C]/80 cursor-pointer">Sono sicuro</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setConfirmAction(null); setDeleteCheck(false); }} disabled={busyAction}>Annulla</Button>
                <Button variant="destructive" onClick={doDelete} disabled={busyAction || !deleteCheck} data-testid="confirm-delete-btn">
                  {busyAction ? "Eliminazione..." : "Elimina"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
